import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
} from "@angular/core";
import {
  NG_VALUE_ACCESSOR,
  ControlValueAccessor,
  FormControl,
  Validators,
} from "@angular/forms";
import {
  AngularEditorConfig,
  AngularEditorComponent,
} from "@kolkov/angular-editor";
import { TranslationModuleEnum } from "src/app/utils/translation-modules.enum";

@Component({
  selector: "app-text-editor",
  templateUrl: "./text-editor.component.html",
  styleUrls: ["./text-editor.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: TextEditorComponent,
      multi: true,
    },
  ],
})
export class TextEditorComponent
  implements ControlValueAccessor, OnInit, AfterViewInit, OnChanges
{
  value: string = "";
  isEditorInitialized: boolean = false;

  translationModulesEnum = TranslationModuleEnum;

  @Input() labelTranslationKey: string;
  @Input() labelTranslationModuleEnum: number;
  @Input() placeholderTranslationKey: string;
  @Input() placeholderTranslationModuleEnum: number;
  @Input() id: string = "";
  @Input() title: string;
  @Input() placeholder: string = "";
  @Input() control: FormControl | undefined;
  @Input() hasAttachments: boolean = true;
  @Input() isAttachmentsRequired: boolean = false;
  @Input() attachmentsFiles: any = [];
  @Input() supportedAttachmentSize: number = 10;
  @Input() supportedAttachmentHint: string;
  @Input() supportedAttachmentTypes: string[] = [
    "image/jpeg",
    "image/png",
    "image/jpg",
    "application/pdf",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/msword",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ];

  @Input() isSubmitted: boolean = false;
  @Input() length: number = 0;
  @Input() minLength: number = 0;
  @Input() maxLength: number = 0;
  @Input() showValidations: boolean = true;
  @Input() isAddArabicVarBtn: boolean = false;
  @Input() isAddEngVarBtn: boolean = false;
  @Input() lang: string = "en";
  @Input() disable: boolean = false;
  @Input() variables: any;
  @Input() editorConfig: AngularEditorConfig = {
    editable: true,
    spellcheck: true,
    height: "150px",
    translate: "yes",
    enableToolbar: true,
    showToolbar: true,
    defaultParagraphSeparator: "",
    defaultFontName: "",
    defaultFontSize: "3",
    sanitize: false,
    outline: false,
    toolbarPosition: "top",
    toolbarHiddenButtons: [
      ["subscript", "superscript", "heading", "fontName"],
      [
        "textColor",
        "backgroundColor",
        "customClasses",
        "insertImage",
        "insertVideo",
        "insertHorizontalRule",
        "removeFormat",
        "toggleEditorMode",
      ],
    ],
  };

  @Output() onKeyup = new EventEmitter();
  @Output() onUpload = new EventEmitter();
  @Output() deleteFile = new EventEmitter();

  savedSelectionRange: Range | null = null;

  @ViewChild("editor") angularEditor: AngularEditorComponent;

  private onChange = (value: string) => {};
  private onTouched = () => {};

  get isRequired() {
    return this.control?.hasValidator(Validators.required);
  }

  get getAttachmentsFiles(): any {
    if (!!this.control?.errors) {
      delete this.control?.errors?.emptyAttachments;
      this.control.setErrors({
        ...this.control?.errors,
      });
      if (this.isAttachmentsRequired && !this.attachmentsFiles.length) {
        this.control.setErrors({
          ...this.control?.errors,
          ...(!this.attachmentsFiles.length && { emptyAttachments: true }),
        });
      }
    } else {
      this.control.setErrors(null);
    }
    return this.attachmentsFiles;
  }

  constructor() {}

  ngOnInit(): void {
    this.editorConfig.editable = !this.disable;
  }

  ngAfterViewInit(): void {
    if (this.angularEditor) {
      this.isEditorInitialized = true;

      const editorElement = this.angularEditor.textArea.nativeElement;

      // Listen for selection changes to store the range
      editorElement.addEventListener("mouseup", () => this.saveSelection());
      editorElement.addEventListener("keyup", () => this.saveSelection());
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["disable"]) {
      this.editorConfig.editable = !this.disable;
    }
  }

  saveSelection() {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      this.savedSelectionRange = selection.getRangeAt(0);
    }
  }
  onVariableSelected(variableCode: string) {
    if (this.isEditorInitialized && this.angularEditor) {
      const editorElement = this.angularEditor.textArea.nativeElement;
      if (editorElement && this.savedSelectionRange) {
        // Focus on the editor
        editorElement.focus();

        // Restore the saved range
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(this.savedSelectionRange);

        // Insert the variable code at the current cursor position
        document.execCommand("insertHTML", false, variableCode);

        // Update the value and trigger change detection
        this.value = editorElement.innerHTML;
        this.onChange(this.value);

        // Save the new selection range after insertion
        this.saveSelection();
      } else {
        console.error(
          "Editor is not initialized properly or no selection range available."
        );
      }
    }
  }

  writeValue(obj: any): void {
    this.value = obj;
    this.onChange(obj);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disable = isDisabled;
    this.editorConfig.editable = !this.disable;
  }

  focus() {
    this.onTouched();
  }

  change() {
    this.onChange(this.value);
  }

  keyup() {
    this.onKeyup.emit(this.value);
  }

  onUploadFile(event) {
    this.onUpload.emit(event);
  }

  onDeleteFile(event) {
    this.deleteFile.emit(event);
  }
}
